/**
 * Clinical → Assignments — eTMF document reviews (ADR-0043).
 *
 * Rows of an eTMF completeness trial assigned to the caller: the full check
 * result for that artifact, the Vault document opened in the app, and
 * "Mark as reviewed" with an optional note. The server stamps actor and time.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Textarea,
} from "@mantine/core";
import { IconCheck, IconFileText } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  completeMyReview,
  fetchReviewDocument,
  listMyReviews,
  vaultDocumentUrl,
  type MyReviewOut,
} from "@/shared/api/etmfCompleteness";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";
import { ArtifactDetail } from "../etmf-completeness/TrialDetail";
import { RAG_COLOR, formatUtcShort, ragOf } from "../etmf-completeness/completenessMeta";

interface Props {
  /** Reports how many reviews are assigned, so the page can pick its empty state. */
  onCount?: (n: number) => void;
}

export function EtmfReviews({ onCount }: Props) {
  const getToken = useAccessToken();
  const [items, setItems] = useState<MyReviewOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewer, setViewer] = useState<{ title: string; url: string; pdf: boolean } | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await listMyReviews(await getToken());
      setItems(res.items);
      onCount?.(res.items.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setItems([]);
      onCount?.(0);
    }
  }, [getToken, onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => () => {
      if (viewer) URL.revokeObjectURL(viewer.url);
    },
    [viewer],
  );

  const openDocument = async (item: MyReviewOut) => {
    setOpening(item.review.review_id);
    try {
      const blob = await fetchReviewDocument(await getToken(), item.review.review_id);
      setViewer({
        title: `${item.row?.document_number ?? ""} ${item.row?.document_name ?? "Document"}`.trim(),
        url: URL.createObjectURL(blob),
        pdf: blob.type.includes("pdf"),
      });
    } catch (err) {
      toastApiError("Could not open the document", err);
    } finally {
      setOpening(null);
    }
  };

  const markReviewed = async (item: MyReviewOut) => {
    const id = item.review.review_id;
    setSaving(id);
    try {
      const updated = await completeMyReview(await getToken(), id, notes[id]?.trim() || null);
      setItems((prev) => (prev ?? []).map((i) => (i.review.review_id === id ? updated : i)));
      toastSuccess("Review recorded", `${item.review.artifact_number} marked as reviewed.`);
    } catch (err) {
      toastApiError("Could not record the review", err);
    } finally {
      setSaving(null);
    }
  };

  if (items === null) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }
  if (error) {
    return (
      <Alert variant="light" color="red" title="Could not load your eTMF reviews">
        {error}
      </Alert>
    );
  }
  if (items.length === 0) return null;

  const open = items.filter((i) => i.review.status !== "reviewed");
  const done = items.filter((i) => i.review.status === "reviewed");

  return (
    <Paper withBorder radius="md" p="md">
      <Text fw={600} mb={4}>
        eTMF document reviews ({open.length} to do{done.length ? `, ${done.length} reviewed` : ""})
      </Text>
      <Text size="sm" c="dimmed" mb="sm">
        Artifacts of an eTMF completeness check assigned to you. Read the results, open the
        document, and mark it reviewed.
      </Text>
      <Accordion variant="separated">
        {[...open, ...done].map((item) => {
          const r = item.review;
          const row = item.row;
          const vaultUrlOf = (docId: string) => vaultDocumentUrl(item.vault_url, docId);
          return (
            <Accordion.Item key={r.review_id} value={r.review_id}>
              <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" pr="md">
                  <div style={{ minWidth: 0 }}>
                    <Text fw={600} size="sm" truncate>
                      {r.artifact_number} {row?.artifact_name ?? ""}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {item.trial_name} · Study {item.study_label} · assigned by{" "}
                      {item.assigned_by_name} · {formatUtcShort(r.assigned_at_utc)}
                    </Text>
                  </div>
                  <Group gap="xs" wrap="nowrap">
                    {row?.overall != null && (
                      <Badge variant="filled" color={RAG_COLOR[ragOf(row.overall)]}>
                        {row.overall}
                      </Badge>
                    )}
                    <Badge variant="light" color={r.status === "reviewed" ? "teal" : "blue"}>
                      {r.status === "reviewed" ? "Reviewed" : "To review"}
                    </Badge>
                  </Group>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  {row ? (
                    <>
                      <Group>
                        <Button
                          leftSection={<IconFileText size={16} />}
                          variant="light"
                          disabled={!row.document_id}
                          loading={opening === r.review_id}
                          onClick={() => void openDocument(item)}
                        >
                          {row.document_id ? "Open document" : "No document filed"}
                        </Button>
                        <Text size="xs" c="dimmed">
                          Results from the check of {formatUtcShort(item.last_checked_at_utc)}
                        </Text>
                      </Group>
                      <ArtifactDetail row={row} vaultUrlOf={vaultUrlOf} />
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      This artifact has no check result yet. It appears here once the trial has been
                      checked.
                    </Text>
                  )}
                  {r.status === "reviewed" ? (
                    <Alert color="teal" variant="light" title="Reviewed">
                      {formatUtcShort(r.reviewed_at_utc)}
                      {r.review_note ? ` — ${r.review_note}` : ""}
                    </Alert>
                  ) : (
                    <Stack gap="xs">
                      <Textarea
                        label="Review note (optional)"
                        placeholder="What you checked, what needs fixing…"
                        autosize
                        minRows={2}
                        maxLength={4000}
                        value={notes[r.review_id] ?? ""}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          setNotes((n) => ({ ...n, [r.review_id]: v }));
                        }}
                      />
                      <Group>
                        <Button
                          leftSection={<IconCheck size={16} />}
                          loading={saving === r.review_id}
                          onClick={() => void markReviewed(item)}
                        >
                          Mark as reviewed
                        </Button>
                      </Group>
                    </Stack>
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          );
        })}
      </Accordion>

      <Modal
        opened={viewer !== null}
        onClose={() => setViewer(null)}
        size="95%"
        centered
        title={<Text fw={700}>{viewer?.title}</Text>}
      >
        {viewer &&
          (viewer.pdf ? (
            <iframe
              title={viewer.title}
              src={viewer.url}
              style={{ width: "100%", height: "80vh", border: 0 }}
            />
          ) : (
            <Stack align="center" p="xl">
              <Text size="sm">This document has no PDF rendition to show in the browser.</Text>
              <Button component="a" href={viewer.url} download>
                Download the file
              </Button>
            </Stack>
          ))}
      </Modal>
    </Paper>
  );
}
